import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { User } from '../../utils/types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Initialize state from localStorage
const getStoredToken = (key: string): string | null => {
  try {
    const token = localStorage.getItem(key);
    // Validate token is not expired before using it
    if (token && key === 'access_token') {
      // Basic check if token looks valid (has 3 parts for JWT)
      const parts = token.split('.');
      if (parts.length !== 3) {
        localStorage.removeItem(key);
        return null;
      }
    }
    return token;
  } catch {
    return null;
  }
};

const getStoredUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch {
    localStorage.removeItem('user');
  }
  return null;
};

const initialState: AuthState = {
  user: getStoredUser(),
  accessToken: getStoredToken('access_token'),
  refreshToken: getStoredToken('refresh_token'),
  isAuthenticated: !!getStoredToken('access_token') && !!getStoredUser(),
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Clear any auth errors
    clearAuthError: (state) => {
      state.error = null;
    },
    
    // Login actions
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    
    loginSuccess: (
      state,
      action: PayloadAction<{ user: User; access: string; refresh: string }>
    ) => {
      const { user, access, refresh } = action.payload;
      
      state.user = user;
      state.accessToken = access;
      state.refreshToken = refresh;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
      
      // Store tokens and user in localStorage
      try {
        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);
        localStorage.setItem('user', JSON.stringify(user));
      } catch (error) {
        console.error('Failed to store tokens:', error);
      }
    },
    
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      // Keep existing auth state - only clear on explicit logout
      // This prevents triggering redirects when login fails
    },
    
    // Logout action
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
      
      // Clear tokens and user from localStorage
      try {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      } catch (error) {
        console.error('Failed to clear tokens:', error);
      }
    },
    
    // Token refresh (SimpleJWT may return a new refresh when ROTATE_REFRESH_TOKENS is on)
    tokenRefreshSuccess: (
      state,
      action: PayloadAction<{ access: string; refresh?: string }>
    ) => {
      const { access, refresh } = action.payload;
      state.accessToken = access;
      if (refresh) {
        state.refreshToken = refresh;
      }

      try {
        localStorage.setItem('access_token', access);
        if (refresh) {
          localStorage.setItem('refresh_token', refresh);
        }
      } catch (error) {
        console.error('Failed to store refreshed token:', error);
      }
    },
    
    // Restore authentication from stored tokens
    restoreAuth: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const {
  clearAuthError,
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  tokenRefreshSuccess,
  restoreAuth,
  clearError,
  setLoading,
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;